import { usePlayerStore } from "./playerStore.js";

/**
 * When Up/Down change the channel in full screen, and when they move into the bar.
 *
 * THE RULE (owner, 2026-10-05): "if the tool[bar]s are open you should only be
 * in navigation and can only change channels with up and down when the bars are
 * not showing". With the bar up and nothing highlighted, Up/Down changed the
 * channel, and a viewer reaching for the bar's buttons zapped away instead.
 *
 * So, with the picture full screen and live:
 *  - bars hidden: Up/Down change the channel;
 *  - bars showing, nothing highlighted: Up/Down step into the bar (Play/Pause),
 *    like OK, Left and Right already did (PlayerSurface);
 *  - in the bar: the arrows move between its controls (spatialNav), as before.
 *
 * Two exceptions keep channel surfing working:
 *  - a zap brings the bar up to name the channel it is going to; the next
 *    Up/Down within ZAP_BURST_MS is still a zap, so Down, Down, Down surfs;
 *  - a bar pinned because the channel is not playing (loading, paused, an
 *    error) does not count: Up/Down still zap away from a channel that will not
 *    play.
 * CH+/CH− always change the channel.
 */

/** How long after a zap the next Up/Down still zaps, though the bar is up. */
export const ZAP_BURST_MS = 2_500;

let lastZapAt = Number.NEGATIVE_INFINITY;

/** Call on every arrow or channel-key zap. */
export function noteZap(): void {
  lastZapAt = performance.now();
}

/** Forget recent zaps (tests: each starts as if nobody had been surfing). */
export function forgetZaps(): void {
  lastZapAt = Number.NEGATIVE_INFINITY;
}

function barUpNow(): boolean {
  const surface = document.querySelector(".player-surface--fullscreen");
  if (!surface || surface.classList.contains("player-surface--idle")) return false;
  if (usePlayerStore.getState().status !== "playing") return false;
  return performance.now() - lastZapAt >= ZAP_BURST_MS;
}

/**
 * The answer as it was when the key arrived. The same press wakes the bar
 * (PlayerSurface), and React redraws between one key listener and the next, so
 * a check made later in the press saw the bar this press had just brought up:
 * Down with the bar hidden went into the bar instead of changing the channel
 * (onn box, 2026-10-05). A capture listener on the window runs before every
 * other listener and keeps the answer for that press.
 */
let atKey: { event: Event; up: boolean } | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (event) => {
    atKey = { event, up: barUpNow() };
  }, true);
}

/** True when this Up/Down press in full screen should go into the bar, not change the channel. */
export function arrowsBelongToBar(event: Event): boolean {
  return atKey?.event === event ? atKey.up : barUpNow();
}

/**
 * The channel a full-screen zap is going to, by name, for the banner that shows
 * it (PlayerSurface). A zap no longer brings the bar up (owner, 2026-10-06), so
 * the Live page's zap and FullscreenZap say where they are going through this.
 */
const zapNameListeners = new Set<(name: string) => void>();
export function announceZap(name: string): void {
  for (const listener of zapNameListeners) listener(name);
}
export function onZapName(listener: (name: string) => void): () => void {
  zapNameListeners.add(listener);
  return () => zapNameListeners.delete(listener);
}
